import React, { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Link,
  useTheme,
  useMediaQuery
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import SEO from '../components/SEO';

const ContactPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#privacy-policy') {
      const privacySection = document.getElementById('privacy-policy');
      if (privacySection) {
        // Wait one frame to ensure layout is settled before scrolling.
        requestAnimationFrame(() => {
          privacySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }
  }, [location.hash]);

  return (
    <Box sx={{ 
      maxWidth: { xs: '100%', md: '1200px' }, 
      mx: 'auto', 
      p: { xs: 2, sm: 3 }
    }}>
      <SEO 
        title="Contact & Help"
        description="Get in touch with the Trainer Academy team. Find answers to FAQs, access help resources, and reach our support for questions or feedback about Pokémon VGC statistics."
        keywords="contact, support, help, faq, trainer academy contact, vgc help, pokemon support"
      />
      <Typography 
        variant={isMobile ? "h4" : "h3"} 
        sx={{ 
          mb: 4, 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
        component="h1"
      >
        Contact & Help
      </Typography>
      
      <Paper sx={{ 
        p: { xs: 2, sm: 4 }, 
        mb: 4, 
        backgroundColor: '#221FC7', 
        color: 'white',
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
      }}>
        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              mb: 3,
              backgroundColor: 'rgba(255,255,255,0.05)',
              p: 3,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)',
                backgroundColor: 'rgba(255,255,255,0.08)',
              }
            }}>
              <EmailIcon sx={{ mr: 2, fontSize: 36, color: '#24CC9F' }} aria-hidden="true" />
              <Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }} component="h2">
                  Contact Us
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Have questions, suggestions, or spotted something we could improve? Don’t hesitate to reach out — we love hearing from you!
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.75)' }}>
                  This application is under active development and may contain bugs or unexpected behavior. If you find any issues or something doesn't work as expected, please report it to support@traineracademy.xyz — every bug report helps us improve.
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    mb: 2,
                    color: '#24CC9F',
                    wordBreak: 'break-word'
                  }}
                  component="h2"
                >
                  support@traineracademy.xyz
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  You can also post in the{' '}
                  <Link component={RouterLink} to="/forum" sx={{ color: '#90caf9', fontWeight: 'bold' }}>
                    Improvements & Bugs forum
                  </Link>
                  {' '}— a great place to report issues, suggest features, or see what others have shared.
                </Typography>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'flex-start',
              backgroundColor: 'rgba(255,255,255,0.05)',
              p: 3,
              borderRadius: 2,
              height: '100%',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'translateY(-5px)',
                backgroundColor: 'rgba(255,255,255,0.08)',
              }
            }}>
              <HelpIcon sx={{ mr: 2, fontSize: 36, color: '#24CC9F' }} aria-hidden="true" />
              <Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 'medium' }} component="h2">
                  Help Resources
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check these resources for assistance with common questions:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                  <Box component="li" sx={{ mb: 1.5 }}>
                    <Link href="https://www.pokemon.com" target="_blank" rel="noopener" sx={{ color: '#24CC9F', '&:hover': { color: '#1aa37f' } }}>
                      Official Pokémon Site
                    </Link>
                  </Box>
                  <Box component="li" sx={{ mb: 1.5 }}>
                    <Link href="https://www.pokemon.com/us/play-pokemon/pokemon-events/" target="_blank" rel="noopener" sx={{ color: '#24CC9F', '&:hover': { color: '#1aa37f' } }}>
                      Pokémon Events / Tournament Finder
                    </Link>
                  </Box>
                  <Box component="li" sx={{ mb: 1.5 }}>
                    <Link href="https://pokemonshowdown.com" target="_blank" rel="noopener" sx={{ color: '#24CC9F', '&:hover': { color: '#1aa37f' } }}>
                      Pokémon Showdown (battle simulator)
                    </Link>
                  </Box>
                  <Box component="li" sx={{ mb: 1.5 }}>
                    <Link href="https://www.smogon.com" target="_blank" rel="noopener" sx={{ color: '#24CC9F', '&:hover': { color: '#1aa37f' } }}>
                      Smogon (competitive resources & usage)
                    </Link>
                  </Box>
                  <Box component="li" sx={{ mb: 1.5 }}>
                    <Link href="https://bulbapedia.bulbagarden.net" target="_blank" rel="noopener" sx={{ color: '#24CC9F', '&:hover': { color: '#1aa37f' } }}>
                      Bulbapedia (encyclopedia)
                    </Link>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center' }}>
                  <Link
                    href="https://x.com/_traineracademy"
                    aria-label="X (Trainer Academy)"
                    sx={{ color: 'white', display: 'flex', alignItems: 'center' }}
                  >
                    <CloseIcon fontSize="large" />
                  </Link>

                  <Link
                    href="https://buymeacoffee.com/traineracademy"
                    target="_blank"
                    rel="noopener"
                    aria-label="Buy Me a Coffee"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    <img
                      src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                      alt="Buy Me a Coffee"
                      style={{ height: 36, border: 0, display: 'block' }}
                    />
                  </Link>

                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 1, 
                      color: 'rgba(255,255,255,0.85)', 
                      maxWidth: 260 
                    }}
                  >
                    If you'd like to support development, you can buy me a coffee.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
        
      </Paper>

      <Paper
        id="privacy-policy"
        sx={{
          p: { xs: 2, sm: 4 },
          mb: 4,
          backgroundColor: '#1a2140',
          color: 'white',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
          border: '1px solid rgba(144, 202, 249, 0.25)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <PrivacyTipIcon sx={{ color: '#24CC9F' }} aria-hidden="true" />
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Privacy Policy
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
          Last updated: April 2, 2026
        </Typography>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Trainer Academy – Turn Assistant collects only the data required to authenticate users and provide
          turn-by-turn win probability analysis in Pokémon Showdown.
        </Typography>

        <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 1.5, color: '#90caf9' }}>
          Data we collect
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, mb: 2 }}>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">Google profile data (email, display name, profile image).</Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">Authentication tokens needed to keep your session active.</Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              Pokémon Showdown battle-state content required to generate predictions.
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">Extension usage settings and daily usage counters.</Typography>
          </Box>
        </Box>

        <Typography variant="h6" component="h3" sx={{ mt: 3, mb: 1.5, color: '#90caf9' }}>
          How we use data
        </Typography>
        <Box component="ul" sx={{ pl: 2.5, mb: 2 }}>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">To sign you in and maintain account session state.</Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">To generate and return battle win probability predictions.</Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">To enforce daily usage limits fairly across users.</Typography>
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 2 }}>
          We do not sell personal data, and we do not use your data for advertising personalization,
          creditworthiness, or lending decisions.
        </Typography>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 2 }}>
          Questions about privacy? Contact{' '}
          <Link href="mailto:support@traineracademy.xyz" sx={{ color: '#24CC9F', fontWeight: 'bold' }}>
            support@traineracademy.xyz
          </Link>
          .
        </Typography>
      </Paper>
      
      <Typography 
        variant="body2" 
        sx={{ 
          textAlign: 'center', 
          color: 'rgba(255,255,255,0.6)', 
          mt: 6,
          mb: 2,
          maxWidth: '700px',
          mx: 'auto'
        }}
      >
        Pokémon Statistics is a fan project. Pokémon and all related properties are © of Nintendo, Game Freak, and The Pokémon Company. 
        This site is not affiliated with or endorsed by Nintendo, Game Freak, or The Pokémon Company.
      </Typography>

      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.6)',
          mt: 2,
          mb: 6,
          maxWidth: '700px',
          mx: 'auto'
        }}
      >
        This project was born as a university final project and is maintained to serve the community. Its primary goal is to provide useful tools and statistics to help players. Development is voluntary — if the site stops meeting that goal, lacks community usefulness, or becomes impossible to maintain, it may no longer receive updates. We welcome feedback, contributions and suggestions to keep the project helpful for everyone.
      </Typography>
    </Box>
  );
};

export default ContactPage;